import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  chooseFilingFolder,
  filingFolderName,
  filingSupported,
  forgetFilingFolder,
} from "@/lib/pdfFiling";

/** Per-computer setting: where this PC files printed PDFs. */
export const PdfFolderSetup = () => {
  const [folder, setFolder] = useState("");
  const supported = filingSupported();

  useEffect(() => {
    filingFolderName().then(setFolder);
  }, []);

  const choose = async () => {
    try {
      setFolder(await chooseFilingFolder());
      toast.success("Saved — invoices and spreadsheets will be filed into this folder from now on");
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Could not use that folder");
    }
  };

  const forget = async () => {
    await forgetFilingFolder();
    setFolder("");
    toast.success("Back to normal downloads on this computer");
  };

  return (
    <div className="mt-6 max-w-lg border border-border p-5" data-testid="pdf-folder-setup">
      <h2 className="text-base font-semibold uppercase tracking-wider md:text-lg">
        Where this computer files invoices
      </h2>
      {!supported ? (
        <p className="mt-2 text-sm text-muted-foreground" data-testid="pdf-folder-unsupported">
          This browser cannot file PDFs into folders. Use Chrome or Edge on the counter
          PCs; until then PDFs go to Downloads as usual.
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm text-muted-foreground">
            Pick your store's folder once. After that every PDF and spreadsheet you
            download is filed into the matching subfolder inside it — Tax Invoice, Loan
            Agreement, Buying Contract, Accountant Export, Reports, Backups — instead of
            landing in Downloads.
          </p>
          {folder ? (
            <p className="mt-3 text-sm" data-testid="pdf-folder-current">
              Filing into <span className="font-semibold">{folder}</span>
            </p>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground" data-testid="pdf-folder-none">
              Not set up on this computer yet.
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              onClick={choose}
              className="h-10 rounded-none bg-[#C5A059] font-semibold text-[#1b1b1b] hover:bg-[#B08F4A]"
              data-testid="pdf-folder-choose-button"
            >
              <FolderOpen className="mr-2 h-4 w-4" />
              {folder ? "Change folder" : "Choose folder"}
            </Button>
            {folder && (
              <Button
                variant="outline"
                onClick={forget}
                className="h-10 rounded-none"
                data-testid="pdf-folder-forget-button"
              >
                Stop filing
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
